import { defineStore } from "pinia";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firestore, functions } from "@/firebase";
import { getPipMultiplier } from "@/components/alerts/utils/alertFormatters";

// Enums matching backend
export enum MessageType {
  ALERT = "alert",
  ORDER_ACTIVATED = "order_activated",
  ADJUSTMENT = "adjustment",
  UPDATE = "update",
  RESULT = "result",
  MANUAL_CLOSE = "manual_close",
  DEACTIVATE_PENDING = "deactivate_pending",
  RESULTS_SUMMARY = "results_summary",
  ANALYSIS = "analysis",
  EDUCATION = "education",
  ANNOUNCEMENT = "announcement",
  OTHER = "other",
}

export enum ResultStatus {
  WIN = "win",
  LOSS = "loss",
  VOID = "void",
  PENDING = "pending",
  PROFIT = "profit",
  BREAKEVEN = "breakeven",
  UNKNOWN = "unknown",
}

export enum AlertType {
  SPORTS_BETTING = "sports_betting",
  FOREX = "forex",
  SYNTHETIC_INDICES = "synthetic_indices",
}

export enum OrderType {
  MARKET = "market",
  BUY_LIMIT = "buy_limit",
  SELL_LIMIT = "sell_limit",
  BUY_STOP = "buy_stop",
  SELL_STOP = "sell_stop",
  PENDING = "pending",
}

export enum MediaType {
  PHOTO = "photo",
  VIDEO = "video",
  DOCUMENT = "document",
  NONE = "none",
}

// UI interfaces
export interface ChannelInfo {
  id: string;
  name: string;
  monitoringEnabled: boolean;
  active: boolean;
  alertsType?: AlertType;
}

export interface TelegramMessageUI {
  messageId: number;
  channelId: string;
  channelName: string;
  date: Date;
  messageType?: MessageType;
  text?: string;
  mediaType?: MediaType;
  mediaUrl?: string;
  storagePath?: string;
  alertType?: AlertType;
  resultStatus?: ResultStatus;
  stake?: string;
  odds?: number;
  sport?: string;
  teams?: string[];
  betType?: string;
  selection?: string;
  linkedMessageIds?: number[]; // IDs of linked messages from relations.linkedMessages
  replyToMessageId?: number; // ID of parent message from relations.replyTo
  aiConfidence?: number; // AI confidence score from analysis.text.confidence (0-1)
  // Forex fields
  pair?: string;
  direction?: string;
  orderType?: OrderType;
  entryPrice?: number;
  stopLoss?: number;
  takeProfit?: number[];
  timeframe?: string;
  exitPrice?: number;
  // Indices fields
  index?: string;
}

export interface GroupedAlert {
  alertId: string;
  mainMessage: TelegramMessageUI;
  relatedMessages: TelegramMessageUI[];
  resultStatus: ResultStatus;
  stake?: string;
  odds?: number;
  hasMedia: boolean;
}

export interface ChannelKPIs {
  totalAlerts: number;
  wins: number;
  losses: number;
  voids: number;
  pending: number;
  winRate: number;
  lossRate: number;
  voidRate: number;
  // Sports-specific
  stakeDistribution?: Record<string, number>;
  yield?: number;
  // General
  averageOdds?: number;
  alertsByType?: Record<string, number>;
  // Forex/Indices
  pips?: number;
}

// Convert Firestore timestamp to Date
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "number") return new Date(value);
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
};

export const useTelegramAlertsStore = defineStore("telegramAlerts", {
  state: () => ({
    channels: [] as ChannelInfo[],
    selectedChannelId: null as string | null,
    messages: [] as TelegramMessageUI[],
    groupedAlerts: [] as GroupedAlert[],
    kpis: null as ChannelKPIs | null,
    isLoading: false,
    filterYear: new Date().getFullYear(),
    filterMonth: new Date().getMonth(),
  }),

  getters: {
    selectedChannel: (state): ChannelInfo | null => {
      return (
        state.channels.find((ch) => ch.id === state.selectedChannelId) || null
      );
    },

    orphanedMessages: (state): TelegramMessageUI[] => {
      // Collect all message IDs that are part of a group
      const groupedMessageIds = new Set<number>();
      for (const group of state.groupedAlerts) {
        groupedMessageIds.add(group.mainMessage.messageId);
        for (const related of group.relatedMessages) {
          groupedMessageIds.add(related.messageId);
        }
      }

      // Return messages that are not in the set
      // And exclude purely informational message types
      const excludedTypes = new Set([
        MessageType.OTHER,
        MessageType.ANNOUNCEMENT,
        MessageType.EDUCATION,
        MessageType.ANALYSIS,
        MessageType.RESULTS_SUMMARY,
      ]);

      return state.messages.filter(
        (msg) =>
          !groupedMessageIds.has(msg.messageId) &&
          (!msg.messageType ||
            !excludedTypes.has(msg.messageType) ||
            (msg.mediaType && msg.mediaType !== MediaType.NONE))
      );
    },
  },

  actions: {
    async fetchChannels() {
      this.isLoading = true;
      try {
        const channelsRef = collection(
          firestore,
          "botsData",
          "telegram",
          "channels"
        );
        const q = query(
          channelsRef,
          where("monitoringEnabled", "==", true),
          where("active", "==", true)
        );
        const snapshot = await getDocs(q);

        this.channels = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || doc.id,
            monitoringEnabled: data.monitoringEnabled || false,
            active: data.active || false,
            alertsType: data.alertsType as AlertType | undefined,
          };
        });

        // Select first channel by default
        if (this.channels.length > 0 && !this.selectedChannelId) {
          this.selectedChannelId = this.channels[0].id;
          await this.fetchMessages();
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchMessages() {
      if (!this.selectedChannelId) return;

      this.isLoading = true;
      try {
        const messagesRef = collection(
          firestore,
          "botsData",
          "telegram",
          "channels",
          this.selectedChannelId,
          "monitoring-messages"
        );

        // Calculate date range for filter
        const startDate = new Date(this.filterYear, this.filterMonth, 1);
        const endDate = new Date(
          this.filterYear,
          this.filterMonth + 1,
          0,
          23,
          59,
          59
        );

        const q = query(
          messagesRef,
          where("date", ">=", Timestamp.fromDate(startDate)),
          where("date", "<=", Timestamp.fromDate(endDate)),
          orderBy("date", "desc"),
          limit(500)
        );

        const snapshot = await getDocs(q);

        this.messages = snapshot.docs.map((doc) => this.mapMessageDoc(doc));

        this.groupAlerts();
        this.calculateKPIs();
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        this.isLoading = false;
      }
    },

    mapMessageDoc(doc: QueryDocumentSnapshot<DocumentData>): TelegramMessageUI {
      const data = doc.data();

      // Extract linked message IDs from relations.linkedMessages
      const linkedMessageIds: number[] = [];
      if (
        data.relations?.linkedMessages &&
        Array.isArray(data.relations.linkedMessages)
      ) {
        for (const link of data.relations.linkedMessages) {
          if (link.messageId) {
            linkedMessageIds.push(link.messageId);
          }
        }
      }

      // Extract replyTo message ID from relations.replyTo (for result messages)
      const replyToMessageId = data.relations?.replyTo
        ? Number(data.relations.replyTo)
        : undefined;

      // Extract AI confidence from analysis.text.confidence
      const aiConfidence =
        data.analysis?.text?.confidence !== undefined
          ? Number(data.analysis.text.confidence)
          : undefined;

      return {
        messageId: data.messageId || 0,
        channelId: data.channelId || "",
        channelName: data.channelName || "",
        date: toDate(data.date),
        messageType: data.messageType as MessageType | undefined,
        text: data.content?.text,
        mediaType: data.content?.media?.type as MediaType | undefined,
        mediaUrl: data.content?.media?.storagePath,
        storagePath: data.content?.media?.storagePath,
        alertType: data.alertType as AlertType | undefined,
        resultStatus: data.alert?.resultStatus as ResultStatus | undefined,
        stake: data.alert?.stake,
        odds: data.alert?.odds?.current?.decimalValue,
        sport: data.alert?.sport,
        teams: data.alert?.teams,
        betType: data.alert?.betType,
        selection: data.alert?.selection,
        // Forex fields
        pair: data.alert?.pair,
        direction: data.alert?.direction,
        orderType: data.alert?.orderType as OrderType | undefined,
        entryPrice: data.alert?.entryPrice,
        stopLoss: data.alert?.stopLoss,
        takeProfit: data.alert?.takeProfit,
        timeframe: data.alert?.timeframe,
        exitPrice: data.alert?.exitPrice,
        index: data.alert?.index,
        linkedMessageIds:
          linkedMessageIds.length > 0 ? linkedMessageIds : undefined,
        replyToMessageId,
        aiConfidence,
      };
    },

    groupAlerts() {
      const alertsMap = new Map<number, GroupedAlert>();
      const messagesById = new Map<number, TelegramMessageUI>();

      // Build a lookup map for all messages
      for (const msg of this.messages) {
        messagesById.set(msg.messageId, msg);
      }

      // First pass: identify main alerts
      for (const msg of this.messages) {
        if (msg.messageType === MessageType.ALERT) {
          alertsMap.set(msg.messageId, {
            alertId: `alert-${msg.messageId}`,
            mainMessage: msg,
            relatedMessages: [],
            resultStatus: msg.resultStatus || ResultStatus.PENDING,
            stake: msg.stake,
            odds: msg.odds,
            hasMedia: msg.mediaType !== MediaType.NONE && !!msg.mediaType,
          });
        }
      }

      // Second pass: link messages using relations.linkedMessages
      for (const alert of alertsMap.values()) {
        const mainMsg = alert.mainMessage;

        // Add linked messages from the relations field
        if (mainMsg.linkedMessageIds && mainMsg.linkedMessageIds.length > 0) {
          for (const linkedId of mainMsg.linkedMessageIds) {
            const linkedMsg = messagesById.get(linkedId);
            if (linkedMsg && linkedMsg.messageId !== mainMsg.messageId) {
              // Check if not already added
              const alreadyAdded = alert.relatedMessages.some(
                (m) => m.messageId === linkedMsg.messageId
              );
              if (!alreadyAdded) {
                alert.relatedMessages.push(linkedMsg);
              }
            }
          }
        }
      }

      // Build a map of MessageID -> GroupedAlert to handle chains (Adjustment -> Adjustment -> Alert)
      const messageToAlertMap = new Map<number, GroupedAlert>();

      // Initialize map with existing grouped messages
      for (const alert of alertsMap.values()) {
        messageToAlertMap.set(alert.mainMessage.messageId, alert);
        for (const related of alert.relatedMessages) {
          messageToAlertMap.set(related.messageId, alert);
        }
      }

      // Third pass: link messages using replyToMessageId recursively
      // We iterate multiple times to handle chains (A -> B -> C -> Alert)
      let changesMade = true;
      let iterations = 0;
      const MAX_ITERATIONS = 20; // Allow deeper chains (matching backend limit of 15+)

      while (changesMade && iterations < MAX_ITERATIONS) {
        changesMade = false;
        iterations++;

        for (const msg of this.messages) {
          // Skip if already grouped
          if (messageToAlertMap.has(msg.messageId)) continue;

          if (msg.replyToMessageId) {
            const parentAlert = messageToAlertMap.get(msg.replyToMessageId);
            if (parentAlert) {
              // Found a parent (which might be an alert or a message linked to an alert)
              // Check if not already added (double check)
              const alreadyAdded = parentAlert.relatedMessages.some(
                (m) => m.messageId === msg.messageId
              );
              if (!alreadyAdded) {
                parentAlert.relatedMessages.push(msg);
                messageToAlertMap.set(msg.messageId, parentAlert);
                changesMade = true;
              }
            }
          }
        }
      }

      // Deduplication pass: Resolve bidirectional links between two ALERT messages
      // If Alert A and Alert B are linked, pick one as the main group and remove the other
      const alertsToRemove = new Set<number>();
      for (const alert of alertsMap.values()) {
        if (alertsToRemove.has(alert.mainMessage.messageId)) continue;

        for (const related of alert.relatedMessages) {
          // Check if the related message is also a main alert group
          if (
            alertsMap.has(related.messageId) &&
            !alertsToRemove.has(related.messageId)
          ) {
            const mainMsg = alert.mainMessage;
            const relatedMsg = related;

            // Decide which one is the "parent"
            // Rule 0: Prefer message with 'selection' parsed (usually the text alert)
            const mainHasSelection = !!mainMsg.selection;
            const relatedHasSelection = !!relatedMsg.selection;

            let keepMain = true;

            if (mainHasSelection && !relatedHasSelection) {
              keepMain = true;
            } else if (!mainHasSelection && relatedHasSelection) {
              keepMain = false;
            } else {
              // Rule 1: Text alerts prioritize over Media-only alerts
              const mainIsMedia =
                mainMsg.mediaType && mainMsg.mediaType !== MediaType.NONE;
              const relatedIsMedia =
                relatedMsg.mediaType && relatedMsg.mediaType !== MediaType.NONE;

              if (!mainIsMedia && relatedIsMedia) {
                keepMain = true;
              } else if (mainIsMedia && !relatedIsMedia) {
                keepMain = false;
              } else {
                // Rule 2: Older messages prioritize over newer messages
                if (mainMsg.date.getTime() <= relatedMsg.date.getTime()) {
                  keepMain = true;
                } else {
                  keepMain = false;
                }
              }
            }

            if (keepMain) {
              alertsToRemove.add(relatedMsg.messageId);
            } else {
              alertsToRemove.add(mainMsg.messageId);
              break; // Stop processing this alert content as it's being removed
            }
          }
        }
      }

      // Remove the duplicate groups
      for (const id of alertsToRemove) {
        alertsMap.delete(id);
      }

      this.groupedAlerts = Array.from(alertsMap.values()).sort(
        (a, b) => b.mainMessage.date.getTime() - a.mainMessage.date.getTime()
      );

      // Recursive chain handling ensures they are grouped, now sort the groups internally
      for (const group of this.groupedAlerts) {
        group.relatedMessages.sort((a, b) => a.messageId - b.messageId);
      }
    },

    calculateKPIs() {
      const alerts = this.groupedAlerts;

      const wins = alerts.filter(
        (a) => a.resultStatus === ResultStatus.WIN
      ).length;
      const losses = alerts.filter(
        (a) => a.resultStatus === ResultStatus.LOSS
      ).length;
      const voids = alerts.filter(
        (a) => a.resultStatus === ResultStatus.VOID
      ).length;
      const pending = alerts.filter(
        (a) => a.resultStatus === ResultStatus.PENDING
      ).length;
      const total = alerts.length;

      const decided = total - pending - voids;

      this.kpis = {
        totalAlerts: total,
        wins,
        losses,
        voids,
        pending,
        winRate: decided > 0 ? (wins / decided) * 100 : 0,
        lossRate: decided > 0 ? (losses / decided) * 100 : 0,
        voidRate: total > 0 ? (voids / total) * 100 : 0,
      };

      // General: Calculate total pips for Forex/Indices
      const channel = this.selectedChannel;
      if (
        channel &&
        (channel.alertsType === AlertType.FOREX ||
          channel.alertsType === AlertType.SYNTHETIC_INDICES)
      ) {
        let totalPips = 0;
        for (const alert of alerts) {
          if (
            alert.resultStatus === ResultStatus.WIN ||
            alert.resultStatus === ResultStatus.LOSS
          ) {
            const msg = alert.mainMessage;
            if (
              msg.entryPrice !== undefined &&
              msg.stopLoss !== undefined &&
              msg.takeProfit &&
              msg.takeProfit.length > 0
            ) {
              const entry = msg.entryPrice;

              // Try to get actual exit price from the alert or related messages
              let exit: number | undefined = msg.exitPrice;

              if (exit === undefined) {
                // Check related messages for exit price
                for (const relatedMsg of alert.relatedMessages) {
                  if (relatedMsg.exitPrice !== undefined) {
                    exit = relatedMsg.exitPrice;
                    break;
                  }
                }
              }

              if (exit === undefined) {
                // Fall back to conservative estimate:
                // For WIN trades, assume TP1 (first take profit) was hit.
                // For LOSS trades, assume SL was hit.
                exit =
                  alert.resultStatus === ResultStatus.WIN
                    ? msg.takeProfit[0]
                    : msg.stopLoss;
              }

              const direction = msg.direction?.toUpperCase();

              // Calculate raw difference
              let diff = exit - entry;
              if (direction === "SELL") {
                diff = entry - exit;
              }

              // Apply multiplier using shared utility
              const type =
                channel.alertsType === AlertType.FOREX ? "forex" : "other";
              const symbol = msg.pair || msg.index;
              const multiplier = getPipMultiplier(symbol, type);

              totalPips += diff * multiplier;
            }
          }
        }
        this.kpis.pips = Number(totalPips.toFixed(1));
      }

      // Calculate stake distribution for sports channels
      if (channel?.alertsType === AlertType.SPORTS_BETTING) {
        const stakeDistribution: Record<string, number> = {};
        for (const alert of alerts) {
          if (alert.stake) {
            stakeDistribution[alert.stake] =
              (stakeDistribution[alert.stake] || 0) + 1;
          }
        }
        this.kpis.stakeDistribution = stakeDistribution;

        // Calculate average odds
        const alertsWithOdds = alerts.filter((a) => a.odds && a.odds > 0);
        const avgOdds =
          alertsWithOdds.length > 0
            ? alertsWithOdds.reduce((sum, a) => sum + (a.odds || 0), 0) /
              alertsWithOdds.length
            : 0;
        this.kpis.averageOdds = avgOdds;

        // Simplified yield calculation
        // NOTE: This assumes uniform stake of 1 unit per bet
        // For accurate yield, backend should provide actual stake amounts in monetary units
        if (decided > 0 && avgOdds > 0) {
          const totalStaked = decided; // Assuming 1 unit per bet
          const winProfit = wins * (avgOdds - 1); // Profit from wins
          const lossAmount = losses; // Loss from losses (1 unit each)
          const netProfit = winProfit - lossAmount;
          this.kpis.yield = (netProfit / totalStaked) * 100;
        } else {
          this.kpis.yield = 0;
        }
      }

      // Alert types distribution
      const typeDistribution: Record<string, number> = {};
      for (const msg of this.messages) {
        if (msg.messageType) {
          typeDistribution[msg.messageType] =
            (typeDistribution[msg.messageType] || 0) + 1;
        }
      }
      this.kpis.alertsByType = typeDistribution;
    },
    async selectChannel(channelId: string) {
      this.selectedChannelId = channelId;
      await this.fetchMessages();
    },

    async updateFilters(year?: number, month?: number) {
      if (year !== undefined) this.filterYear = year;
      if (month !== undefined) this.filterMonth = month;
      await this.fetchMessages();
    },

    async reprocessMessage(channelId: string, messageId: number) {
      try {
        // Call the cloud function to reprocess the message using Firebase Functions
        const reprocessSingleMessage = httpsCallable(
          functions,
          "TelegramMonitoring-reprocessSingleMessage"
        );

        const result = await reprocessSingleMessage({
          channelId,
          messageId,
        });

        // Refresh messages after reprocessing
        await this.fetchMessages();

        return result.data;
      } catch (error) {
        console.error("Error reprocessing message:", error);
        throw error;
      }
    },

    async linkMessages(
      channelId: string,
      targetMessageId: number, // Parent alert
      sourceMessageId: number, // Orphan message,
      linkType?: "odds_source" | "result" | "supplementary" | null
    ) {
      try {
        const linkMessagesManually = httpsCallable(
          functions,
          "TelegramMonitoring-linkMessagesManually"
        );

        const result = await linkMessagesManually({
          channelId,
          targetMessageId,
          sourceMessageId,
          linkType,
        });

        // Refresh messages after linking
        await this.fetchMessages();

        return result.data;
      } catch (error) {
        console.error("Error linking messages:", error);
        throw error;
      }
    },

    async updateAlertManually(
      channelId: string,
      messageId: number,
      data: {
        stake?: string;
        odds?: number;
        resultStatus?: ResultStatus;
        // Forex fields
        pair?: string;
        direction?: string;
        orderType?: OrderType;
        entryPrice?: number;
        stopLoss?: number;
        takeProfit?: number[];
        exitPrice?: number;
        index?: string;
      }
    ) {
      try {
        const updateAlertManuallyFn = httpsCallable(
          functions,
          "TelegramMonitoring-updateAlertManually"
        );

        await updateAlertManuallyFn({
          channelId,
          messageId,
          data,
        });

        // Refresh messages after update
        await this.fetchMessages();
      } catch (error) {
        console.error("Error updating alert manually:", error);
        throw error;
      }
    },
  },
});
